import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { Bell } from "lucide-react";
import React, { useEffect, useState } from "react";
import NotificationsDialog from "./Dialog";
import { Notification, Orders } from "@/interface/entityInterface";
import { useGetList, useRefresh } from "react-admin";
import { Checkbox } from "@/components/ui/checkbox";
import axiosInstance from "@/utils/axiosInstance";
import OrderDetails from "@/taxi-dispatch/orders/OrderDetails";
import AgentOrderDetails from "@/tours-agent/orders/OrderDetails";
import { useUserContext } from "@/context/UserContext";

const NotificationsList = () => {
  const refresh = useRefresh();
  const { decodedToken } = useUserContext();
  const [toggleSwitch, setToggle] = useState<boolean>(false);
  const { data: notifications = [] } = useGetList<Notification>(
    "notifications/unread",
    {
      pagination: { page: 1, perPage: 100 },
      filter: {
        toggleSwitch,
      },
    },
  );
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<Notification | null>(null);
  const [orderId, setOrderId] = useState<number>(0);
  const [order, setOrder] = useState<Orders | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);

  const readNotification = async (id: number) => {
    try {
      await axiosInstance.post(`/notifications/read/${id}`);
      refresh();
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!orderId) return;
    const getOrder = async () => {
      const { data } = await axiosInstance.get<Orders>(
        `/taxi-orders/${orderId}`,
      );
      setOrder(data);
      setOrderOpen(true);
    };

    getOrder();
  }, [orderId]);

  useEffect(() => {
    if (!orderOpen) setOrderId(0);
  }, [orderOpen]);

  return (
    <div className="p-5 max-w-screen-2xl mx-auto grid gap-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-y-3">
          <div className="flex items-center gap-x-2">
            <Checkbox
              checked={toggleSwitch}
              onCheckedChange={() => setToggle((prev) => !prev)}
            />
            <p className="font-medium text-gray-400">Show only unread</p>
          </div>
          {notifications.length > 0 &&
            notifications.map((item) => (
              <Alert
                className="cursor-pointer"
                key={item.id}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!item.seen) readNotification(item.id);
                  if (
                    item.type.includes("ORDER") ||
                    item.type.includes("DRIVER") ||
                    item.type.includes("PAID_FARE")
                  )
                    setOrderId(
                      typeof item.data === "string"
                        ? JSON.parse(item.data).taxiOrderId || JSON.parse(item.data).orderId
                        : item?.data.taxiOrderId,
                    );
                  else {
                    setRow(item);
                    setOpen(true);
                  }
                }}
              >
                <Bell className={`${!item.seen && "!text-red-600"}`} />
                <div className="flex items-center justify-between">
                  <AlertTitle>{item.title}</AlertTitle>
                  <AlertDescription>
                    {format(item.createdAt, "dd MMMM yyyy hh:mm")}
                  </AlertDescription>
                </div>
                <AlertDescription>{item.message}</AlertDescription>
              </Alert>
            ))}
        </CardContent>
      </Card>
      {open && (
        <NotificationsDialog open={open} setOpen={setOpen} notification={row} />
      )}
      {decodedToken?.permissions.settings_type === "taxi" && (
        <OrderDetails
          isSheetOpen={orderOpen}
          setIsSheetOpen={setOrderOpen}
          selectedOrder={order}
        />
      )}
      {decodedToken?.permissions.settings_type === "transportation_agency" && (
        <AgentOrderDetails
          isSheetOpen={orderOpen}
          setIsSheetOpen={setOrderOpen}
          selectedOrder={order}
        />
      )}
    </div>
  );
};

export default NotificationsList;
